import DetailViewModel from "../../detailViewModel";
import LocalizationService from "services/localizationService";
import type { interfaces } from "inversify";
import { Modules } from "config";
import AccountingDocumentCandidateDetailResponse from "entities/accountingDocumentCandidateDetailResponse";
import AccountingDocumentCandidatesRepository from "repositories/accountingDocumentCandidatesRepository";
import { computed, observable } from "mobx";
import AccountingDocumentCandidateRowsViewModel from "./accountingDocumentCandidateRowsViewModel";
import type PagingManagerViewModel from "../../pagingManagerViewModel";
import { bound } from "@frui.ts/helpers";
import type AccountingDocumentsApprovalsViewModel from "./accountingDocumentsApprovalsViewModel";
import AccountingDocumentsApprovalService from "services/accountingDocumentsApprovalService";
import type { IWithPrevNextNavigationViewModel } from "../../types";
import OptionsService from "services/optionsService";
import AccountingTemplateResponse from "entities/accountingTemplateResponse";
import { watchBusy } from "@frui.ts/screens";

export default class AccountingDocumentApprovalDetailViewModel
  extends DetailViewModel<AccountingDocumentCandidateDetailResponse>
  implements IWithPrevNextNavigationViewModel
{
  readonly modelName = "accountingDocumentCandidate";
  @observable accountingDocumentsRows: AccountingDocumentCandidateRowsViewModel;

  @computed
  get name() {
    return this.translate("candidateDetail");
  }

  @computed
  get hasPrevious() {
    return this.pagingManagerViewModel.hasPrevious;
  }

  @computed
  get hasNext() {
    return this.pagingManagerViewModel.hasNext;
  }

  @computed
  get allowApprove() {
    return this.approvalService.allowWrite();
  }

  constructor(
    protected itemId: string,
    private pagingManagerViewModel: PagingManagerViewModel<AccountingDocumentsApprovalsViewModel>,
    private accountingDocumentsRowsDetailViewModelFactory: ReturnType<typeof AccountingDocumentCandidateRowsViewModel.Factory>,
    protected localizationService: LocalizationService,
    private optionsService: OptionsService,
    private repository: AccountingDocumentCandidatesRepository,
    private approvalService: AccountingDocumentsApprovalService
  ) {
    super(itemId);
    this.accountingDocumentsRows = this.accountingDocumentsRowsDetailViewModelFactory(itemId);
  }

  onInitialize() {
    this.accountingDocumentsRows.initialize();
    return super.onInitialize();
  }

  @bound
  onApproveClick(item: AccountingDocumentCandidateDetailResponse) {
    return async () => {
      await this.approvalService.approve(this.repository, item, {
        title: this.translate("confirmApproval.title"),
        confirm: this.translate("confirmApproval.confirm"),
        approved: this.translate("approved"),
      });
    };
  }

  @bound
  canApprove(item: AccountingDocumentCandidateDetailResponse) {
    return this.approvalService.canApprove(item);
  }

  @bound
  approvedByMe(item: AccountingDocumentCandidateDetailResponse) {
    return this.approvalService.approvedByMe(item);
  }

  @bound
  isExported(item: AccountingDocumentCandidateDetailResponse) {
    return false;
  }

  @bound
  myApprovalStep(item: AccountingDocumentCandidateDetailResponse) {
    return this.approvalService.myApprovalStep(item);
  }

  @bound
  @watchBusy
  async onNextClick() {
    const itemId = await this.pagingManagerViewModel.onNextClick();

    if (itemId) {
      this.loadItem(itemId);
    }
  }

  @bound
  @watchBusy
  async onPreviousClick() {
    const itemId = await this.pagingManagerViewModel.onPreviousClick();

    if (itemId) {
      this.loadItem(itemId);
    }
  }

  async loadItem(itemId: string) {
    this.itemId = itemId;
    this.accountingDocumentsRows = this.accountingDocumentsRowsDetailViewModelFactory(itemId);
    this.accountingDocumentsRows.initialize();
    const item = await this.loadDetail();
    this.setItem(item);
  }

  protected loadDetail(): Promise<AccountingDocumentCandidateDetailResponse> {
    return this.repository.get(this.itemId);
  }

  @bound
  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `accountingDocumentsApprovals.${code}`, size);
  }

  translateAccountingTemplate(accountingTemplateId: string) {
    return this.optionsService.entityOptions(AccountingTemplateResponse).find(item => item.id === accountingTemplateId)?.label;
  }

  static Factory({ container }: interfaces.Context) {
    return (id: string, pagingManagerViewModel: PagingManagerViewModel<any>) => {
      return new AccountingDocumentApprovalDetailViewModel(
        id,
        pagingManagerViewModel,
        container.get(AccountingDocumentCandidateRowsViewModel.Factory),
        container.get(LocalizationService),
        container.get(OptionsService),
        container.get(AccountingDocumentCandidatesRepository),
        container.get(AccountingDocumentsApprovalService)
      );
    };
  }
}
